import { cn } from "@/lib/utils";
import type {
  SubscriptionLedgerStatus,
  SubscriptionPaymentMethod,
  SubscriptionPlanType,
  SubscriptionState,
} from "@/types";

/* ─── Formatting ─── */

export function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}.`;
}

export function fmtMoney(n: number): string {
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/* ─── Labels ─── */

export const PLAN_LABEL: Record<SubscriptionPlanType, string> = {
  annual: "Godišnja",
  semiannual: "Polugodišnja",
  custom: "Ručno",
};

export function planLabel(plan: SubscriptionPlanType | null): string {
  return plan ? PLAN_LABEL[plan] : "—";
}

export const METHOD_LABEL: Record<SubscriptionPaymentMethod, string> = {
  monri: "Kartica (Monri)",
  bank_transfer: "Uplatnica",
  manual: "Ručno",
};

/* ─── Badges ─── */

type BadgeTone = "emerald" | "rose" | "amber" | "muted";

const TONE_CLS: Record<BadgeTone, { pill: string; dot: string }> = {
  emerald: { pill: "bg-emerald-50 text-emerald-800 ring-emerald-200/70", dot: "bg-emerald-500" },
  rose: { pill: "bg-rose-50 text-rose-800 ring-rose-200/70", dot: "bg-rose-500" },
  amber: { pill: "bg-amber-50 text-amber-900 ring-amber-200/70", dot: "bg-amber-500" },
  muted: { pill: "bg-muted/60 text-muted-foreground ring-border/70", dot: "bg-muted-foreground/50" },
};

export const STATE_BADGE: Record<SubscriptionState, { label: string; tone: BadgeTone }> = {
  active: { label: "Aktivna", tone: "emerald" },
  expired: { label: "Istekla", tone: "rose" },
  never_paid: { label: "Nije plaćena", tone: "amber" },
  exempt: { label: "Oslobođen", tone: "muted" },
};

export const STATUS_BADGE: Record<SubscriptionLedgerStatus, { label: string; tone: BadgeTone }> = {
  pending: { label: "Na čekanju", tone: "amber" },
  completed: { label: "Plaćeno", tone: "emerald" },
  failed: { label: "Neuspjelo", tone: "rose" },
  refunded: { label: "Refundirano", tone: "muted" },
};

export function Pill({
  label,
  tone,
  className,
}: {
  label: string;
  tone: BadgeTone;
  className?: string;
}) {
  const cls = TONE_CLS[tone];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset",
        cls.pill,
        className,
      )}
    >
      <span className={cn("size-1.5 rounded-full", cls.dot)} />
      {label}
    </span>
  );
}

/* ─── Actions ─── */

/**
 * Pill-shaped row action in the payouts PayButton style: an icon badge
 * inside a pill that fills with color on hover.
 */
export function PillAction({
  icon: Icon,
  onClick,
  tone = "primary",
  children,
  className,
}: {
  icon: React.ComponentType<{ className?: string; weight?: "bold" }>;
  onClick: () => void;
  tone?: "primary" | "danger";
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border bg-card pl-1.5 pr-3 text-[11px] font-semibold transition-all duration-200 active:scale-[0.96]",
        tone === "primary"
          ? "border-primary/25 text-primary hover:border-primary hover:bg-primary hover:text-primary-foreground hover:shadow-md hover:shadow-primary/25"
          : "border-rose-200 text-rose-600 hover:border-rose-600 hover:bg-rose-600 hover:text-white hover:shadow-md hover:shadow-rose-600/25",
        className,
      )}
    >
      <span
        className={cn(
          "flex size-[18px] items-center justify-center rounded-full transition-all duration-200 group-hover:bg-white/20",
          tone === "primary" ? "bg-primary/10" : "bg-rose-100",
        )}
      >
        <Icon weight="bold" className="size-3" />
      </span>
      {children}
    </button>
  );
}

/* ─── Table cells ─── */

export function Th({
  children,
  align = "left",
  className,
}: {
  children?: React.ReactNode;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <th
      className={cn(
        "px-3 py-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground",
        align === "right" ? "text-right" : "text-left",
        className,
      )}
    >
      {children}
    </th>
  );
}

/** Amount with a small unit suffix, payouts-table style. */
export function MoneyValue({
  value,
  currency = "KM",
  className,
}: {
  value: number;
  currency?: string;
  className?: string;
}) {
  return (
    <span className={cn("tabular-nums text-foreground", className)}>
      {fmtMoney(value)}
      <span className="ml-0.5 text-[11px] font-normal text-muted-foreground">
        {currency}
      </span>
    </span>
  );
}

/** Labeled value for mobile cards: tiny uppercase label above the value. */
export function MobileField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 truncate text-sm text-foreground">{children}</div>
    </div>
  );
}
